import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';

const STORAGE_KEY = 'figshelf_uat_site_gate_unlocked';

async function sha256Hex(input) {
  if (typeof window === 'undefined') return null;
  if (!window.crypto || !window.crypto.subtle) return null;
  const encoder = new TextEncoder();
  const data = encoder.encode(String(input ?? ''));
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function UatPasswordGate({ children }) {
  const enabled = useMemo(() => {
    const v = String(process.env.REACT_APP_UAT_PASSWORD_ENABLED ?? '').toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }, []);

  const expectedPassword = useMemo(() => {
    return String(process.env.REACT_APP_UAT_PASSWORD ?? '').trim();
  }, []);

  const expectedSha256 = useMemo(() => {
    return String(process.env.REACT_APP_UAT_PASSWORD_SHA256 ?? '').trim().toLowerCase();
  }, []);

  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!enabled) return;

    setSubmitting(true);
    setError('');

    try {
      const input = String(password ?? '');

      if (expectedPassword) {
        if (input === expectedPassword) {
          setUnlocked(true);
          try {
            localStorage.setItem(STORAGE_KEY, '1');
          } catch {}
          return;
        }
        setError('Incorrect password');
        return;
      }

      if (expectedSha256) {
        const digest = await sha256Hex(input);
        if (digest && digest.toLowerCase() === expectedSha256) {
          setUnlocked(true);
          try {
            localStorage.setItem(STORAGE_KEY, '1');
          } catch {}
          return;
        }
        setError('Incorrect password');
        return;
      }

      setError('UAT password gate is enabled but no password is configured.');
    } finally {
      setSubmitting(false);
    }
  }, [enabled, expectedPassword, expectedSha256, password]);

  if (!enabled || unlocked) return children;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: 420, p: 3 }} elevation={6}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Figshelf UAT Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the UAT password to continue.
        </Typography>

        <TextField
          fullWidth
          type="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          error={!!error}
          helperText={error || ' '}
          autoFocus
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !password}
          sx={{ mt: 1.5 }}
        >
          {submitting ? 'Checking…' : 'Continue'}
        </Button>
      </Paper>
    </Box>
  );
}
